'use client'

import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import {
  BASE_SANITIZE_OPTIONS,
  CUSTOM_TEXT_ENDING,
  getPlainText,
  truncateHtml,
} from '@/lib/utils/text-utils'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { IOptions } from 'sanitize-html'
import sanitizeHtml from 'sanitize-html'
import { ReadMoreLink } from './ReadMoreLink.client'

const DEFAULT_MAX_LENGTH = 150

type BehaviorProps =
  | {
      type: 'expand'
      targetId?: never
    }
  | {
      type: 'navigate'
      targetId: `#${string}`
    }

export type ReadMoreProps = BehaviorProps & {
  /** The text that will be shortened if it exceeds DEFAULT_MAX_LENGTH characters */
  text: string

  /** Based on being the equivalent of roughly 3 lines in mobile layout */
  maxLength?: number

  /** Allow rendering of dangerous HTML */
  allowHtml?: boolean

  /** Optional ref for the portal target (if not provided, will use the content div) */
  portalRef?: React.RefObject<HTMLDivElement | null>

  /** Callback to set the truncated state */
  setIsTruncated?: (isTruncated: boolean) => void
  /** Optional tracking data pushed when clicking the read more action */
  trackingData?: TrackingGaEvent
  /** Custom sanitize options (if not provided, default options will be used) */
  sanitizeOptions?: IOptions
}

export function ReadMore({
  text,
  targetId,
  maxLength = DEFAULT_MAX_LENGTH,
  allowHtml = false,
  portalRef,
  setIsTruncated,
  trackingData,
  sanitizeOptions = BASE_SANITIZE_OPTIONS,
}: ReadMoreProps): React.ReactNode {
  const [isPortalReady, setIsPortalReady] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const sanitizedHtml = allowHtml ? sanitizeHtml(text, sanitizeOptions) : ''
  const plainText = allowHtml ? getPlainText(sanitizedHtml) : text
  const isTruncated = plainText.length > maxLength
  const truncatedHtml = allowHtml && isTruncated ? truncateHtml(sanitizedHtml, maxLength) : ''

  useEffect(() => {
    setIsTruncated?.(isTruncated)
  }, [text, setIsTruncated, isTruncated])

  useEffect(() => {
    if (portalRef?.current) {
      setIsPortalReady(true)
    }
  }, [portalRef])

  // ReadMoreLink component wrapped in span
  const readMoreLink = (
    <span className="text-lg">
      {targetId ? (
        <ReadMoreLink type="navigate" targetId={targetId} trackingData={trackingData} />
      ) : (
        <ReadMoreLink
          type="expand"
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          trackingData={trackingData}
        />
      )}
    </span>
  )

  if (!allowHtml) {
    if (!isTruncated) {
      return <p>{text}</p>
    }

    const truncatedText = text.slice(0, maxLength) + CUSTOM_TEXT_ENDING

    return (
      <span>
        {truncatedText}
        {readMoreLink}
      </span>
    )
  }

  if (!sanitizedHtml) {
    return null
  }

  if (!isTruncated) {
    return <div className="wysiwyg min-w-fit" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  }

  return (
    <div className="flex w-full flex-row flex-wrap items-end">
      <div
        className="wysiwyg min-w-fit"
        dangerouslySetInnerHTML={{ __html: isExpanded ? sanitizedHtml : truncatedHtml }}
      />

      {isPortalReady && portalRef?.current
        ? createPortal(readMoreLink, portalRef.current)
        : readMoreLink}
    </div>
  )
}
